import { Component, inject, input } from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { injectQuery } from "@tanstack/angular-query-experimental";

import { CURRENT_LOCALE_ID } from "@intocare/angular-data/lib/current-locale-id";
import { injectCloseActionOverlay } from "@intocare/angular-routing/lib/overlay-navigation";
import {
	CareDataService,
	EvaluationMeasureAction,
	evaluationMeasureConceptToDisplayWith,
} from "@intocare/data-access/portal/care";
import { LegacyDataAccess } from "@intocare/data-access/portal/legacy";
import {
	DrawerComponent,
	IcdsQueryDataFromDirective,
	IcdsQueryDataLoadingDirective,
	IcdsQueryStateComponent,
	OverlayContainer,
	OverlayHeaderTemplateDirective,
	PageSectionHeadingComponent,
	PageSubsectionComponent,
	PageSubsectionDividerComponent,
	PageSubsectionHeadingSkeleton,
	SimpleButtonComponent,
} from "@intocare/design/design-system-base";
import {
	generateRestrictiveMeasuresReport,
	getRestrictiveMeasuresFileName,
	RestrictiveMeasureReportItem,
} from "@intocare/features/pdf-generation";
import { injectResident } from "@intocare/residents/data";
import { CONTEXT_TOKEN } from "@intocare/util";

import { RestrictiveMeasuresOverlaySkeleton } from "./restrictive-measures-overlay.skeleton";
import {
	RestrictiveMeasureListComponent,
	RestrictiveMeasureListItem,
} from "../components/restrictive-measure-list.component";

@Component({
	selector: "ic-restrictive-measures",
	imports: [
		OverlayHeaderTemplateDirective,
		IcdsQueryDataFromDirective,
		IcdsQueryDataLoadingDirective,
		DrawerComponent,
		TranslateModule,
		IcdsQueryStateComponent,
		PageSectionHeadingComponent,
		RestrictiveMeasureListComponent,
		PageSubsectionDividerComponent,
		PageSubsectionComponent,
		PageSubsectionHeadingSkeleton,
		RestrictiveMeasuresOverlaySkeleton,
		SimpleButtonComponent,
	],
	templateUrl: "restrictive-measures.overlay.html",
})
export class RestrictiveMeasuresOverlay extends OverlayContainer {
	private readonly legacyDataAccess = inject(LegacyDataAccess);
	private readonly currentLocaleId = inject(CURRENT_LOCALE_ID);
	private readonly careDataService = inject(CareDataService);
	readonly closeActionOverlay = injectCloseActionOverlay();
	private readonly context = inject(CONTEXT_TOKEN);

	readonly siteId = input.required<string>();
	readonly residentId = input.required<string>({ alias: "resident" });
	protected readonly resident = injectResident(this.residentId);

	protected readonly careTreeValueSetQuery = injectQuery(() =>
		this.legacyDataAccess.careTreeValueSetQueryOptions({ locale: this.currentLocaleId() }),
	);

	protected readonly restrictiveMeasuresForResidentQuery = injectQuery(() =>
		this.careDataService.restrictiveMeasuresForResidentQueryOptions(
			{ siteId: this.siteId() },
			{ residentId: this.residentId() },
		),
	);

	private toReportItem(measure: RestrictiveMeasureListItem): RestrictiveMeasureReportItem {
		const locale = this.currentLocaleId();

		return {
			measureTitle: evaluationMeasureConceptToDisplayWith({
				locale,
				careTreeValueSet: this.careTreeValueSetQuery.data(),
			})(measure.measure),
			data: measure,
		};
	}

	protected readonly onDownloadReport = (
		measures: RestrictiveMeasureListItem[],
		documentType: Extract<EvaluationMeasureAction, "ACTIVE" | "STOPPED">,
	) => {
		if (measures.length === 0) {
			return;
		}

		const resident = this.resident();

		if (!resident) {
			return;
		}

		const locale = this.currentLocaleId();
		const ctx = this.context.context();
		const user = `${ctx.user.firstName} ${ctx.user.name}`;

		void generateRestrictiveMeasuresReport(
			{
				resident: { fullName: resident.fullName },
				measures: measures.map((m) => this.toReportItem(m)),
				user,
				documentType,
			},
			locale,
		).download(getRestrictiveMeasuresFileName(resident.fullName, documentType, locale));
	};
}
